import React, { useCallback, useEffect, useState, useId, useRef } from "react";
import PropTypes from "prop-types";

import { SearchHelpIcon } from "../icons";

import Input from "./input.jsx";
import InputWrapper from "./input-wrapper.jsx";
import { Box, Typography } from "@mui/material";

const SearchHelpInput = ({
    className = "",
    fullWidth = true,
    shrink = true,
    label,
    value,
    delay = 900,
    error = false,
    errorMessage,
    warning = false,
    warningMessage,
    disabled,
    showFormHelpers = true,
    showInnerHelpers = false,
    IconComponent = SearchHelpIcon,
    onChange,
    onOpenSearchHelp,
    onCheckValue
}) => {
    const id = useId();

    const [valueInner, setValueInner] = useState("");
    const [changeEvent, setChangeEvent] = useState(null);
    const isTypingRef = useRef(false);
    const abortControllerRef = useRef(null);
    const checkTimeoutRef = useRef(null);
    const changeTimeoutRef = useRef(null);
    const previousValueRef = useRef(value);
    const lastCheckedValueRef = useRef(null);

    const flushPendingChanges = useCallback(() => {
        if (changeTimeoutRef.current) {
            clearTimeout(changeTimeoutRef.current);
            changeTimeoutRef.current = null;
        }

        if (changeEvent) {
            onChange(changeEvent);
            setChangeEvent(null);
            isTypingRef.current = false;
        }
    }, [changeEvent, onChange]);

    useEffect(() => {
        if (!isTypingRef.current) {
            setValueInner(value);
        }
    }, [value]);

    useEffect(() => {
        const currentValue = value || "";
        const previousValue = previousValueRef.current || "";
        
        if (currentValue !== previousValue && !isTypingRef.current) {
            previousValueRef.current = value;

            if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const shouldCheck = value && 
                                onCheckValue && 
                                currentValue.toLowerCase() !== (lastCheckedValueRef.current || "").toLowerCase();

            if (shouldCheck) {
                checkTimeoutRef.current = setTimeout(() => {
                    abortControllerRef.current = new AbortController();
                    lastCheckedValueRef.current = currentValue;
                    onCheckValue(value, abortControllerRef.current.signal);
                    checkTimeoutRef.current = null;
                }, delay);
            }
        }
    }, [value, delay, onCheckValue]);

    useEffect(() => {
        if (!changeEvent) return;

        changeTimeoutRef.current = setTimeout(() => {
            onChange(changeEvent);
            setChangeEvent(null);
            isTypingRef.current = false;
            changeTimeoutRef.current = null;
        }, delay);

        return () => {
            if (changeTimeoutRef.current) {
                clearTimeout(changeTimeoutRef.current);
            }
        };
    }, [changeEvent, delay, onChange]);

    useEffect(() => {
        return () => {
            if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
            }
            if (changeTimeoutRef.current) {
                clearTimeout(changeTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const onChangeInner = useCallback((e) => {
        const value = e.target.value;
        
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        
        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
            checkTimeoutRef.current = null;
        }
        
        lastCheckedValueRef.current = null;
        
        isTypingRef.current = true;
        setValueInner(value);
        setChangeEvent(e);
    }, []);

    const onBlur = useCallback(() => {
        flushPendingChanges();
        
        if (valueInner && onCheckValue) {
            const shouldCheck = valueInner.toLowerCase() !== (lastCheckedValueRef.current || "").toLowerCase();
            
            if (shouldCheck) {
                lastCheckedValueRef.current = valueInner;
                onCheckValue(valueInner, null);
            }
        }
    }, [valueInner, onCheckValue, flushPendingChanges]);

    const onCLickSearchHelpIcon = useCallback(() => {
        if (disabled) return;

        flushPendingChanges();

        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
            checkTimeoutRef.current = null;
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        onOpenSearchHelp();
    }, [disabled, onOpenSearchHelp, flushPendingChanges]);

    return (
        <InputWrapper
            id={id}
            className={className}
            fullWidth={fullWidth}
            shrink={shrink}
            label={label}
            error={error}
            errorMessage={errorMessage}
            warning={warning}
            warningMessage={warningMessage}
            showFormHelpers={showFormHelpers}
            InputComponent={
                <Input
                    id={id}
                    value={valueInner}
                    onChange={onChangeInner}
                    onBlur={onBlur}
                    error={error}
                    errorMessage={errorMessage}
                    warning={warning}
                    warningMessage={warningMessage}
                    disabled={disabled}
                    showInnerHelpers={showInnerHelpers}
                    endAdornment={
                        <IconComponent
                            onClick={onCLickSearchHelpIcon}
                            sx={{
                                cursor: "pointer",
                                color: "#B7B7B7"
                            }}
                        />
                    }
                />
            }
        />
    );
};

const SearchHelpDescriptionInput = ({
    className = "",
    fullWidth = true,
    shrink = true,
    label,
    value,
    description,
    delay = 900,
    error = false,
    errorMessage,
    warning = false,
    warningMessage,
    disabled,
    showFormHelpers = true,
    showInnerHelpers = false,
    IconComponent = SearchHelpIcon,
    onChange,
    onOpenSearchHelp,
    onCheckValue
}) => {
    const id = useId();

    const [valueInner, setValueInner] = useState("");
    const [changeEvent, setChangeEvent] = useState(null);
    const isTypingRef = useRef(false);
    const abortControllerRef = useRef(null);
    const checkTimeoutRef = useRef(null);
    const changeTimeoutRef = useRef(null);
    const previousValueRef = useRef(value);
    const lastCheckedValueRef = useRef(null);

    const flushPendingChanges = useCallback(() => {
        if (changeTimeoutRef.current) {
            clearTimeout(changeTimeoutRef.current);
            changeTimeoutRef.current = null;
        }

        if (changeEvent) {
            onChange(changeEvent);
            setChangeEvent(null);
            isTypingRef.current = false;
        }
    }, [changeEvent, onChange]);

    useEffect(() => {
        if (!isTypingRef.current) {
            setValueInner(value);
        }
    }, [value]);

    useEffect(() => {
        if (value !== previousValueRef.current && !isTypingRef.current) {
            previousValueRef.current = value;

            if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
            }

            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            const currentValue = value || "";
            const shouldCheck = value && 
                                onCheckValue && 
                                currentValue.toLowerCase() !== (lastCheckedValueRef.current || "").toLowerCase();

            if (shouldCheck) {
                checkTimeoutRef.current = setTimeout(() => {
                    abortControllerRef.current = new AbortController();
                    lastCheckedValueRef.current = currentValue;
                    onCheckValue(value, abortControllerRef.current.signal);
                    checkTimeoutRef.current = null;
                }, delay);
            }
        }
    }, [value, delay, onCheckValue]);

    useEffect(() => {
        if (!changeEvent) return;

        changeTimeoutRef.current = setTimeout(() => {
            onChange(changeEvent);
            setChangeEvent(null);
            isTypingRef.current = false;
            changeTimeoutRef.current = null;
        }, delay);

        return () => {
            if (changeTimeoutRef.current) {
                clearTimeout(changeTimeoutRef.current);
            }
        };
    }, [changeEvent, delay, onChange]);

    useEffect(() => {
        return () => {
            if (checkTimeoutRef.current) {
                clearTimeout(checkTimeoutRef.current);
            }
            if (changeTimeoutRef.current) {
                clearTimeout(changeTimeoutRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    const onChangeInner = useCallback((e) => {
        const value = e.target.value;
        
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        
        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
            checkTimeoutRef.current = null;
        }
        
        lastCheckedValueRef.current = null;
        
        isTypingRef.current = true;
        setValueInner(value);
        setChangeEvent(e);
    }, []);

    const onBlur = useCallback(() => {
        flushPendingChanges();

        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
            checkTimeoutRef.current = null;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        if (valueInner && onCheckValue) {
            const shouldCheck = valueInner.toLowerCase() !== (lastCheckedValueRef.current || "").toLowerCase();
            
            if (shouldCheck) {
                abortControllerRef.current = new AbortController();
                lastCheckedValueRef.current = valueInner;
                onCheckValue(valueInner, abortControllerRef.current.signal);
            }
        }
    }, [valueInner, onCheckValue, flushPendingChanges]);

    const onCLickSearchHelpIcon = useCallback(() => {
        if (disabled) return;

        flushPendingChanges();

        if (checkTimeoutRef.current) {
            clearTimeout(checkTimeoutRef.current);
            checkTimeoutRef.current = null;
        }
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }

        onOpenSearchHelp();
    }, [disabled, onOpenSearchHelp, flushPendingChanges]);

    return (
        <InputWrapper
            id={id}
            className={className}
            fullWidth={fullWidth}
            shrink={shrink}
            label={label}
            error={error}
            errorMessage={errorMessage}
            warning={warning}
            warningMessage={warningMessage}
            showFormHelpers={showFormHelpers}
            InputComponent={
                <Box sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center"
                }}>
                    <Input
                        id={id}
                        sx={{
                            width: "50%"
                        }}
                        value={valueInner}
                        onChange={onChangeInner}
                        onBlur={onBlur}
                        error={error}
                        errorMessage={errorMessage}
                        warning={warning}
                        warningMessage={warningMessage}
                        disabled={disabled}
                        showInnerHelpers={showInnerHelpers}
                        endAdornment={
                            <IconComponent
                                onClick={onCLickSearchHelpIcon}
                                sx={{
                                    cursor: "pointer",
                                    color: "#B7B7B7"
                                }}
                            />
                        }
                    />

                    <Typography className="search-help-input-description">
                        {description}
                    </Typography>
                </Box>
            }
        />
    );
};

SearchHelpInput.propTypes = {
    className: PropTypes.string,
    fullWidth: PropTypes.bool,
    shrink: PropTypes.bool,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    delay: PropTypes.number,
    onOpenSearchHelp: PropTypes.func,
    onCheckValue: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    warning: PropTypes.bool,
    warningMessage: PropTypes.string,
    showFormHelpers: PropTypes.bool,
    showInnerHelpers: PropTypes.bool,
    IconComponent: PropTypes.elementType
};

SearchHelpDescriptionInput.propTypes = {
    className: PropTypes.string,
    fullWidth: PropTypes.bool,
    shrink: PropTypes.bool,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    delay: PropTypes.number,
    onOpenSearchHelp: PropTypes.func,
    onCheckValue: PropTypes.func,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    warning: PropTypes.bool,
    warningMessage: PropTypes.string,
    description: PropTypes.string,
    showFormHelpers: PropTypes.bool,
    showInnerHelpers: PropTypes.bool,
    IconComponent: PropTypes.elementType
};

export default SearchHelpInput;
export {
    SearchHelpDescriptionInput
};
